import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Shield, FileCheck, Handshake, Sparkles, Lock, UserCheck, ShieldCheck } from "lucide-react";
import { nexusApi } from "@/context/NexusAuthContext";
import ListingCard from "@/components/nexus/ListingCard";
import { CATEGORIES } from "@/pages/nexus/meta";
import useSEO, { canonicalFor } from "@/lib/useSEO";
import "@/pages/nexus/nexus.css";

export default function NexusHome() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const nav = useNavigate();

  useSEO({
    title: "Corpora Nexus — Marketplace for Regulated & Intangible Assets",
    description: "Curated listings of licensed entities, business stakes, IP, fund structures, and commercial assets. Free to list — success fee only. All introductions under NDA.",
    canonical: canonicalFor("/nexus"),
    image: canonicalFor("/brand/nexus-og.jpg"),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Corpora Nexus — Marketplace for Regulated & Intangible Assets",
      about: "Curated marketplace for licensed entities, business stakes, IP, fund structures and commercial assets",
      publisher: { "@type": "Organization", name: "Corpora Consultants" },
      url: canonicalFor("/nexus"),
    },
  });

  useEffect(() => {
    nexusApi.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    nexusApi.get("/listings?limit=6").then((r) => {
      const items = [...(r.data.items || [])].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).slice(0, 3);
      setFeatured(items);
    }).catch(() => {});
  }, []);

  const goMarketplace = () => nav("/nexus/listings");

  return (
    <div data-testid="nexus-home">
      {/* ══════════ HERO ══════════ */}
      <section className="nx-hero">
        <div className="nx-container nx-hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <div className="nx-eyebrow rule mb-8">{t("nexus.home.heroEyebrow")} · Est. MMXXVI</div>
          </motion.div>

          <motion.h1
            className="nx-h1 max-w-[16ch]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.2, 0.7, 0.2, 1] }}
          >
            {t("nexus.home.heroTitle")}
          </motion.h1>

          <motion.p
            className="nx-lead mt-10"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28 }}
          >
            {t("nexus.home.heroLead")}
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-3 mt-14"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.42 }}
          >
            <button onClick={goMarketplace} className="nx-btn nx-btn-primary" data-testid="hero-cta-browse">
              {t("nexus.home.enterMarketplace")} <ArrowRight size={14} />
            </button>
            <Link to="/nexus/sell" className="nx-btn nx-btn-secondary" data-testid="hero-cta-sell">
              {t("nexus.home.listYourAsset")}
            </Link>
          </motion.div>

          <motion.div
            className="mt-10 flex items-center gap-3 text-xs"
            style={{ color: "var(--bone-2)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.75 }}
            transition={{ duration: 0.8, delay: 0.65 }}
          >
            <Lock size={12} className="text-[var(--pewter)]" />
            <span className="font-mono uppercase tracking-[0.24em]">{t("nexus.home.heroBadge")}</span>
          </motion.div>
        </div>
      </section>

      {/* ══════════ LIQUIDITY BAR ══════════ */}
      <section className="nx-container -mt-1">
        <LiquidityBar stats={{ active_listings: 24, jurisdictions: 14 }} />
      </section>

      {/* ══════════ LIVE TICKER ══════════ */}
      <div className="mt-24">
        <LiveTicker />
      </div>

      {/* ══════════ FEATURED ══════════ */}
      <section className="nx-section">
        <div className="nx-container">
          <MotionEyebrow>{t("nexus.home.featured")}</MotionEyebrow>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6 mb-14">
            <h2 className="nx-h2 max-w-[24ch]">{t("nexus.home.aFewOfWhatsLive")}</h2>
            <button onClick={goMarketplace} className="nx-btn nx-btn-ghost">
              {t("nexus.home.viewAll")} <ArrowRight size={12} />
            </button>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {featured.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <ListingCard listing={l} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CATEGORIES ══════════ */}
      <section className="nx-section-tight">
        <div className="nx-container">
          <MotionEyebrow>Asset classes</MotionEyebrow>
          <h2 className="nx-h2 mt-4 mb-14 max-w-[28ch]">Six categories. One curated marketplace.</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <motion.button
                key={c.id}
                onClick={() => nav(`/nexus/listings?category=${c.id}`)}
                data-testid={`nexus-cat-${c.id}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="nx-card text-left"
              >
                <div className="font-serif text-2xl text-[var(--bone)] mb-3">{c.label}</div>
                <div className="nx-eyebrow">Explore →</div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section id="how" className="nx-section">
        <div className="nx-container">
          <MotionEyebrow>Process</MotionEyebrow>
          <h2 className="nx-h2 mt-4 mb-14 max-w-[26ch]">Four stages. Confidential end-to-end.</h2>

          <div className="grid gap-0 md:grid-cols-4 border-t border-[var(--line)] border-b">
            {[
              { icon: UserCheck,  n: "01", t: "Qualify",  b: "Short buyer registration. Serious enquiries only." },
              { icon: Shield,     n: "02", t: "NDA",      b: "Sign a mutual confidentiality agreement with the seller or their agent. We provide and manage all documentation." },
              { icon: FileCheck,  n: "03", t: "Review",   b: "Access the full pack, financials and asking terms." },
              { icon: Handshake,  n: "04", t: "Transact", b: "Corpora Consultants advisory available at any stage — diligence, structuring and close." },
            ].map(({ icon: Icon, n, t, b }, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="p-8 border-r border-[var(--line)] md:border-r last:md:border-r-0 border-b md:border-b-0"
              >
                <div className="flex items-center justify-between mb-6">
                  <Icon size={18} className="text-[var(--pewter)]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--bone-2)]/50">{n}</span>
                </div>
                <div className="font-serif text-xl text-[var(--bone)] mb-3">{t}</div>
                <p className="text-[var(--bone-2)] text-sm leading-relaxed">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ ASSURANCE STRIP (replaces testimonials) ══════════ */}
      <section className="nx-section">
        <div className="nx-container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="nx-card"
            style={{ borderColor: "rgba(180,154,111,0.28)" }}
            data-testid="nexus-assurance-strip"
          >
            <div className="flex items-start gap-4">
              <ShieldCheck size={22} className="text-[#C9896E] mt-1 shrink-0" />
              <p className="text-[var(--bone-2)] leading-relaxed text-base md:text-lg max-w-[68ch]">
                {t("nexus.home.assuranceStrip")}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════ TRUST ROW ══════════ */}
      <section className="nx-section-tight">
        <div className="nx-container">
          <MotionEyebrow>How we protect confidentiality</MotionEyebrow>
          <h2 className="nx-h2 mt-4 mb-14 max-w-[28ch]">Discretion is a design choice.</h2>
          <div className="nx-trust">
            {[
              { icon: Lock,       t: "Layered disclosure", b: "Teaser only in public. Full pack unlocks after account + NDA." },
              { icon: Shield,     t: "IP-logged NDAs",     b: "Every acceptance timestamped, typed-signed and IP-logged." },
              { icon: UserCheck,  t: "Qualified buyers",   b: "Registration + qualification form before any listing view." },
              { icon: FileCheck,  t: "Counsel built in",   b: "Corpora Consultants Ltd advises every mandate end-to-end." },
            ].map(({ icon: Icon, t, b }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="nx-trust-cell"
              >
                <Icon size={18} className="nx-trust-icon" />
                <div className="nx-trust-title">{t}</div>
                <p className="nx-trust-sub">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FOR SELLERS ══════════ */}
      <section className="nx-section">
        <div className="nx-container">
          <motion.div
            className="nx-card nx-card-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            style={{ borderColor: "rgba(180,154,111,0.32)" }}
          >
            <div className="grid gap-10 md:grid-cols-5 items-center">
              <div className="md:col-span-3">
                <div className="nx-eyebrow rule mb-6">For sellers & agents</div>
                <h2 className="nx-h2 mb-5 max-w-[22ch]">Bring us an asset. Success fee only.</h2>
                <p className="nx-lead">No listing fees. No exclusivity trap. Full confidentiality. Corpora curates, drafts the teaser, handles the NDA framework, and manages every buyer intro.</p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <Link to="/nexus/sell" className="nx-btn nx-btn-primary" data-testid="sellers-cta">
                  Submit your asset <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// ══════════ Sub-components ══════════

function MotionEyebrow({ children }) {
  return (
    <motion.div
      className="nx-eyebrow rule"
      initial={{ opacity: 0, x: -12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55 }}
    >
      {children}
    </motion.div>
  );
}

function LiquidityBar({ stats }) {
  const { t } = useTranslation();
  const cells = [
    { n: stats.active_listings, l: t("nexus.home.activeListings") },
    { n: stats.jurisdictions,   l: t("nexus.home.jurisdictions") },
  ];
  return (
    <div className="nx-liq" data-testid="nexus-stats">
      {cells.map(({ n, l }, i) => (
        <div className="nx-liq-cell" key={i}>
          <span className="nx-liq-num" data-testid={`nexus-stats-${i}`}>{n}</span>
          <span className="nx-liq-lbl">{l}</span>
        </div>
      ))}
    </div>
  );
}

function LiveTicker() {
  // Rotating listing titles with category tags — no activity events, no dates.
  const items = [
    { title: "Cyprus CySEC CIF (MiFID II + MiCA)",           tag: "EU · CIF" },
    { title: "Cyprus CySEC CASP — MiCA Authorised",          tag: "EU · CASP" },
    { title: "Mauritius IDL — Full Service (Banking Included)", tag: "Mid-shore · IDL" },
    { title: "Mauritius IDL — Full Service (2025)",          tag: "Mid-shore · IDL" },
    { title: "Mauritius IDL — Clean Entity (2026)",          tag: "Mid-shore · IDL" },
    { title: "Seychelles Securities Dealer — Option A (2024)", tag: "Offshore · SD" },
    { title: "Seychelles Securities Dealer — Option B (2023)", tag: "Offshore · SD" },
    { title: "Seychelles Securities Dealer — FX Market Maker", tag: "Offshore · SD" },
    { title: "Comoros Forex/CFD Licence (MISA)",             tag: "Offshore · Forex" },
    { title: "South Africa FSP Licence (FSCA)",              tag: "Africa · FSP" },
    { title: "MSB — Freshly Approved (Valid to 2030)",       tag: "North America · MSB" },
  ];
  // duplicate items for seamless loop
  const track = [...items, ...items];
  return (
    <div className="nx-ticker" data-testid="nexus-ticker">
      <div className="nx-ticker-track">
        {track.map((it, i) => (
          <div className="nx-ticker-item" key={i}>
            <span className="dot" />
            <span style={{ color: "var(--bone)" }}>{it.title}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span style={{ color: "var(--pewter)" }}>{it.tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
